import { FC, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Stack, Typography, useTheme } from '@mui/material';
import {
  GridGroupNode,
  GridRenderCellParams,
  gridDimensionsSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';

import dayjs from 'dayjs';

import { ReactComponent as MapPinIcon } from '@/assets/icons/map-pin-outline-icon.svg';
import GeneratikGroupingCell from '@/shared/ui/GeneratikTable/GeneratikGroupingCell';
import { StatusChip } from '@/shared/ui/StatusChip';

import { PendingPickupGroup, PendingPickupRow } from '../../../model/pendingPickups';
import PendingPickupActions, { PendingPickupActionHandlers } from './PendingPickupActions';
import formatPackageCounts from './formatPackageCounts';

interface PendingPickupGroupCellProps extends PendingPickupActionHandlers {
  params: GridRenderCellParams;
  group: PendingPickupGroup | undefined;
}

const TEXT_SX = {
  fontSize: '0.875rem',
  color: 'grey.800',
  whiteSpace: 'nowrap',
  flexShrink: 0,
} as const;

const HUG_CHIP_SX = {
  '&, & .MuiChip-label': { minWidth: '0 !important' },
} as const;

const ROW_END_PADDING = '1rem';
const LEAF_ACTIONS_OFFSET = '-2.5rem';

const formatWeight = (weight: number): string => String(Math.round(weight * 100) / 100);

const PendingPickupGroupCell: FC<PendingPickupGroupCellProps> = ({
  params,
  group,
  onPrint,
  onView,
  onVoid,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const node = params.rowNode as GridGroupNode;
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = useState(0);

  const apiRef = useGridApiContext();
  const viewportWidth = useGridSelector(apiRef, gridDimensionsSelector).viewportInnerSize.width;

  useLayoutEffect(() => {
    const content = contentRef.current;
    const scroller = content?.closest('.MuiDataGrid-virtualScroller');
    if (!content || !scroller) return;
    const offset = content.getBoundingClientRect().left - scroller.getBoundingClientRect().left;
    setContentWidth(scroller.clientWidth - offset);
  }, [group, viewportWidth]);

  if (node.type !== 'group') {
    const row = params.row as PendingPickupRow | undefined;
    return row?.shipment ? (
      <Stack
        direction="row"
        alignItems="center"
        height="100%"
        ml={LEAF_ACTIONS_OFFSET}
        position="relative"
        zIndex={1}
      >
        <PendingPickupActions row={row} onPrint={onPrint} onView={onView} onVoid={onVoid} />
      </Stack>
    ) : null;
  }

  if (!group) return <GeneratikGroupingCell {...params} />;

  const { order } = group;
  const pickupDay = dayjs(order.pickupDate);
  const dayLabel = pickupDay.isSame(dayjs(), 'day')
    ? t('awb.today')
    : pickupDay.format('D MMM YYYY');

  return (
    <GeneratikGroupingCell {...params} showLabel={false}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap="1rem"
        flexShrink={0}
        ref={contentRef}
        sx={{
          width: `calc(${contentWidth}px - ${ROW_END_PADDING})`,
        }}
        data-testid="pending-pickup-group"
      >
        <Stack direction="row" alignItems="center" gap="1rem" flexShrink={0}>
          <Stack direction="row" alignItems="center" gap="0.5rem" flexShrink={0}>
            <MapPinIcon style={{ width: 22, height: 22, flexShrink: 0 }} />
            <Typography sx={TEXT_SX}>{group.senderName}</Typography>
          </Stack>
          <StatusChip
            label={t('awb.awb_count', { count: group.awbCount })}
            bg={theme.palette.blue[500]}
            sx={HUG_CHIP_SX}
            testId="pending-pickup-group-count"
          />
          <Typography sx={TEXT_SX}>
            {t('awb.group_totals', {
              packages: formatPackageCounts(t, group.parcels, group.envelopes),
              weight: formatWeight(group.weight),
            })}
          </Typography>
        </Stack>

        {group.ordered && (
          <StatusChip
            label={
              order.pickupHourTo
                ? t('awb.pickup_requested_at', { day: dayLabel, hour: order.pickupHourTo })
                : t('awb.pickup_requested_on', { day: dayLabel })
            }
            bg={theme.palette.green[500]}
            sx={HUG_CHIP_SX}
            testId="pending-pickup-group-ordered"
          />
        )}
      </Stack>
    </GeneratikGroupingCell>
  );
};

export default PendingPickupGroupCell;
